import {useEffect, useRef, useState} from 'react';

type UseRecurringCallbackOptions =
  | {
      callback: () => void | Promise<unknown>;
      intervalSeconds: number;
      runImmediately?: boolean;
      maxRuns?: number;
      onError?: (error: unknown) => void;
      restartKey?: string;
    }
  | {skip: true};

export const useRecurringCallback = (
  options: UseRecurringCallbackOptions,
): {secondsUntilRun: number | null} => {
  const [secondsUntilRun, setSecondsUntilRun] = useState<number | null>(null);
  const callbackRef = useRef<(() => void | Promise<unknown>) | null>(null);
  const onErrorRef = useRef<((error: unknown) => void) | undefined>(undefined);
  const runsRef = useRef(0);
  const countRef = useRef(0);

  const skip = 'skip' in options;
  const intervalSeconds = skip ? 0 : options.intervalSeconds;
  const runImmediately = skip ? false : options.runImmediately ?? false;
  const maxRuns = skip ? undefined : options.maxRuns;
  const restartKey = skip ? undefined : options.restartKey;

  if (!skip) {
    callbackRef.current = options.callback;
    onErrorRef.current = options.onError;
  }

  useEffect(() => {
    if (skip) {
      setSecondsUntilRun(null);
      return;
    }

    runsRef.current = 0;

    const isDone = () => maxRuns !== undefined && runsRef.current >= maxRuns;

    const invoke = async () => {
      if (isDone()) return;
      runsRef.current += 1;
      try {
        await callbackRef.current?.();
      } catch (err) {
        onErrorRef.current?.(err);
      }
    };

    if (runImmediately) {
      invoke();
    }

    if (isDone()) {
      setSecondsUntilRun(null);
      return;
    }

    countRef.current = intervalSeconds;
    setSecondsUntilRun(intervalSeconds);

    const interval = setInterval(() => {
      countRef.current -= 1;
      if (countRef.current <= 0) {
        invoke();
        if (isDone()) {
          clearInterval(interval);
          setSecondsUntilRun(null);
          return;
        }
        countRef.current = intervalSeconds;
      }
      setSecondsUntilRun(countRef.current);
    }, 1000);

    return () => clearInterval(interval);
  }, [skip, intervalSeconds, runImmediately, maxRuns, restartKey]);

  return {secondsUntilRun};
};
